import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  message,
  Modal,
  Tag,
  Space,
  Row,
  Col,
  Select,
  Input,
  Card,
  Tooltip,
  Popconfirm,
  Statistic,
  Divider,
  Badge,
  Typography,
  List,
  Form,
  InputNumber,
  Switch,
  DatePicker,
  Collapse,
  Descriptions
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  DashboardOutlined,
  TrophyOutlined,
  RiseOutlined,
  SafetyOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  FolderOpenOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  FileOutlined,
  FullscreenOutlined, // ✅ YENİ İKON
  FullscreenExitOutlined // ✅ YENİ İKON
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import apiClient, { handleApiError } from '../utils/axiosConfig';

const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const SupplierPerformanceDataList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // ✅ YENİ STATE - FULLSCREEN MODAL
  const [fullscreenModalVisible, setFullscreenModalVisible] = useState(false);

  // ✅ Fetch suppliers on mount
  useEffect(() => {
    fetchSuppliers();
  }, []);

  // ✅ Fetch performance data on mount
  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchSuppliers = async () => {
    try {
      console.log('🔄 Fetching suppliers...');
      const response = await apiClient.get('/api/Suppliers/suppliers-only');
      const suppliersData = response.data || [];
      console.log('✅ Suppliers loaded:', suppliersData.length);
      setSuppliers(suppliersData);
    } catch (error) {
      const errorMessage = handleApiError(error, t('error_fetching_suppliers'));
      console.error('❌ Error:', error);
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/SupplierPerformance');
      console.log('📊 Performance Data Response:', response.data);

      const performanceData = response.data || [];

      if (!performanceData || performanceData.length === 0) {
        message.warning(t('no_performance_data_available'));
        setDataSource([]);
        setFilteredData([]);
        return;
      }

      // Extract unique periods
      const uniquePeriods = [...new Set(performanceData.map(p => p.period))].sort().reverse();
      setPeriods(uniquePeriods);

      // Format data for table
      const formattedData = performanceData.map((item, index) => ({
        key: index,
        performanceDataId: item.performanceDataId,
        supplierId: item.supplierId,
        supplierName: item.supplier?.supplierName || 'N/A',
        supplierCode: item.supplier?.supplierCode || 'N/A',
        period: item.period,
        productRevenue: item.productRevenue,
        productQuantity: item.productQuantity,
        defectProductQuantity: item.defectProductQuantity,
        ppm: item.ppm,
        targetPPM: item.targetPPM,
        returnCost: item.returnCost,
        returnCostPaid: item.returnCostPaid,
        auditPerformance: item.auditPerformance,
        productSafety: item.productSafety,
        eSPICEDeviations: item.eSPICEDeviations,
        developedProjectsCount: item.developedProjectsCount,
        manufacturingMaturityCount: item.manufacturingMaturityCount,
        targetKPIAchievement: item.targetKPIAchievement,
        capaClosureDays: item.capaClosureDays,
        isNewSupplier: item.isNewSupplier,
        hasIATF16949: item.hasIATF16949,
        hasISO14001: item.hasISO14001,
        totalScore: item.totalScore,
        supplierClass: item.supplierClass,
        auditFrequency: item.auditFrequency,
        riskAssessment: item.riskAssessment,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      }));

      setDataSource(formattedData);
      setFilteredData(formattedData);
      console.log('✅ Performance data loaded:', formattedData.length);
    } catch (error) {
      const errorMessage = handleApiError(error, t('error_loading_data'));
      message.error(errorMessage);
      console.error('Error fetching performance data:', error);
      setDataSource([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch attachments for selected performance data
  const fetchAttachments = async (performanceDataId) => {
    setLoadingAttachments(true);
    try {
      console.log('📎 Fetching attachments for:', performanceDataId);
      const response = await apiClient.get(`/api/PerformanceAttachment/performance/${performanceDataId}`);
      console.log('✅ Attachments loaded:', response.data);
      setAttachments(response.data || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  // ✅ Apply filters
  const applyFilters = () => {
    let filtered = dataSource;

    if (supplierFilter) {
      filtered = filtered.filter(item => item.supplierId === supplierFilter);
    }

    if (periodFilter) {
      filtered = filtered.filter(item => item.period === periodFilter);
    }

    if (classFilter) {
      filtered = filtered.filter(item => item.supplierClass === classFilter);
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    applyFilters();
  }, [supplierFilter, periodFilter, classFilter, dataSource]);

  const handleSupplierChange = (value) => {
    setSupplierFilter(value);
  };

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
  };

  const handleClassChange = (value) => {
    setClassFilter(value);
  };

  const handleSearch = (value) => {
    const filtered = dataSource.filter(item =>
      item.supplierName.toLowerCase().includes(value.toLowerCase()) ||
      item.supplierCode.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  // ✅ Delete performance data
  const handleDelete = async (performanceDataId) => {
    try {
      await apiClient.delete(`/api/SupplierPerformance/${performanceDataId}`);
      message.success(t('performance_data_deleted_successfully'));
      fetchPerformanceData();
      console.log('✅ Performance data deleted:', performanceDataId);
    } catch (error) {
      const errorMessage = handleApiError(error, t('error_deleting_performance_data'));
      message.error(errorMessage);
      console.error('Error deleting:', error);
    }
  };

  // ✅ Show detail modal
  const showDetailModal = async (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
    await fetchAttachments(record.performanceDataId);
  };

  // ✅ Show edit modal
  const showEditModal = (record) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      ...record,
      period: moment(record.period, 'YYYY-MM')
    });
    setEditModalVisible(true);
  };

  // ✅ Handle edit save
  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        supplierId: selectedRecord.supplierId,
        period: values.period.format('YYYY-MM'),
        productRevenue: values.productRevenue,
        productQuantity: values.productQuantity,
        defectProductQuantity: values.defectProductQuantity,
        targetPPM: values.targetPPM,
        returnCost: values.returnCost || 0,
        returnCostPaid: values.returnCostPaid || false,
        auditPerformance: values.auditPerformance,
        productSafety: values.productSafety,
        eSPICEDeviations: values.eSPICEDeviations,
        developedProjectsCount: values.developedProjectsCount,
        manufacturingMaturityCount: values.manufacturingMaturityCount,
        targetKPIAchievement: values.targetKPIAchievement,
        capaClosureDays: values.capaClosureDays,
        isNewSupplier: values.isNewSupplier || false,
        hasIATF16949: values.hasIATF16949 || false,
        hasISO14001: values.hasISO14001 || false
      };

      await apiClient.put(`/api/SupplierPerformance/${selectedRecord.performanceDataId}`, payload);
      message.success(t('performance_data_updated_successfully'));
      setEditModalVisible(false);
      fetchPerformanceData();
    } catch (error) {
      const errorMessage = handleApiError(error, t('error_updating_performance_data'));
      message.error(errorMessage);
      console.error('Error updating:', error);
    } finally {
      setSaving(false);
    }
  };

  // ✅ Download attachment
  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const response = await apiClient.get(`/api/PerformanceAttachment/download/${attachmentId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success(t('file_downloaded_successfully'));
    } catch (error) {
      message.error(t('error_downloading_file'));
      console.error('Error downloading:', error);
    }
  };

  // ✅ Risk color helper
  const getRiskColor = (risk) => {
    if (risk === 1) return 'success';
    if (risk === 3) return 'warning';
    if (risk === 9) return 'error';
    return 'default';
  };

  const getRiskIcon = (risk) => {
    if (risk === 1) return <CheckCircleOutlined />;
    if (risk === 3) return <WarningOutlined />;
    if (risk === 9) return <CloseCircleOutlined />;
    return null;
  };

  const getClassColor = (supplierClass) => {
    if (supplierClass === 'A') return 'success';
    if (supplierClass === 'B') return 'warning';
    if (supplierClass === 'C') return 'error';
    return 'default';
  };

  const getRiskLevelColor = (level) => {
    if (level === 'Low') return 'success';
    if (level === 'Medium') return 'warning';
    if (level === 'High') return 'error';
    return 'default';
  };

  // ✅ Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // 🔥 TÜM KOLONLAR - FULL LİSTE
  const columns = [
    {
      title: t('supplier_name'),
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.supplierCode}</div>
        </div>
      )
    },
    {
      title: t('period'),
      dataIndex: 'period',
      key: 'period',
      width: 120,
      fixed: 'left',
      sorter: (a, b) => a.period.localeCompare(b.period),
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: t('product_revenue_usd'),
      dataIndex: 'productRevenue',
      key: 'productRevenue',
      width: 150,
      sorter: (a, b) => a.productRevenue - b.productRevenue,
      render: (revenue) => `$${revenue?.toLocaleString() || 0}`
    },
    {
      title: t('product_quantity'),
      dataIndex: 'productQuantity',
      key: 'productQuantity',
      width: 130,
      sorter: (a, b) => a.productQuantity - b.productQuantity,
      render: (qty) => qty?.toLocaleString() || 0
    },
    {
      title: t('product_quantity_risk'),
      dataIndex: 'riskAssessment',
      key: 'productQuantityRisk',
      width: 120,
      render: (riskAssessment) => {
        const risk = riskAssessment?.productQuantityRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: t('defect_product_quantity'),
      dataIndex: 'defectProductQuantity',
      key: 'defectProductQuantity',
      width: 150,
      sorter: (a, b) => a.defectProductQuantity - b.defectProductQuantity
    },
    {
      title: 'PPM (Auto)',
      dataIndex: 'ppm',
      key: 'ppm',
      width: 120,
      sorter: (a, b) => (a.ppm || 0) - (b.ppm || 0),
      render: (ppm) => (
        <Tag color={ppm > 100 ? 'error' : 'success'}>
          {ppm?.toFixed(2) || 'N/A'}
        </Tag>
      )
    },
    {
      title: t('target_ppm'),
      dataIndex: 'targetPPM',
      key: 'targetPPM',
      width: 120,
      sorter: (a, b) => a.targetPPM - b.targetPPM
    },
    {
      title: t('ppm_risk'),
      dataIndex: 'riskAssessment',
      key: 'ppmRisk',
      width: 100,
      render: (riskAssessment) => {
        const risk = riskAssessment?.ppmRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: t('return_cost'),
      dataIndex: 'returnCost',
      key: 'returnCost',
      width: 130,
      sorter: (a, b) => a.returnCost - b.returnCost,
      render: (cost) => `$${cost?.toLocaleString() || 0}`
    },
    {
      title: t('return_cost_paid'),
      dataIndex: 'returnCostPaid',
      key: 'returnCostPaid',
      width: 120,
      render: (paid) => (
        <Tag color={paid ? 'success' : 'default'}>
          {paid ? t('yes') : t('no')}
        </Tag>
      )
    },
    {
      title: t('audit_performance'),
      dataIndex: 'auditPerformance',
      key: 'auditPerformance',
      width: 140,
      sorter: (a, b) => a.auditPerformance - b.auditPerformance,
      render: (perf) => (
        <Tag color={perf >= 80 ? 'success' : perf >= 70 ? 'warning' : 'error'}>
          {perf}%
        </Tag>
      )
    },
    {
      title: t('audit_risk'),
      dataIndex: 'riskAssessment',
      key: 'auditRisk',
      width: 100,
      render: (riskAssessment) => {
        const risk = riskAssessment?.auditRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: t('product_safety'),
      dataIndex: 'productSafety',
      key: 'productSafety',
      width: 120,
      render: (safety) => (
        <Tag color={getRiskColor(safety)}>
          {safety || 'N/A'}
        </Tag>
      )
    },
    {
      title: t('e_spice_deviations'),
      dataIndex: 'eSPICEDeviations',
      key: 'eSPICEDeviations',
      width: 140,
      render: (deviation) => (
        <Tag color={getRiskColor(deviation)}>
          {deviation || 'N/A'}
        </Tag>
      )
    },
    {
      title: t('developed_projects_count'),
      dataIndex: 'developedProjectsCount',
      key: 'developedProjectsCount',
      width: 150,
      sorter: (a, b) => a.developedProjectsCount - b.developedProjectsCount
    },
    {
      title: t('developed_projects_risk'),
      dataIndex: 'riskAssessment',
      key: 'developedProjectsRisk',
      width: 150,
      render: (riskAssessment) => {
        const risk = riskAssessment?.developedProjectsRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: t('manufacturing_maturity_count'),
      dataIndex: 'manufacturingMaturityCount',
      key: 'manufacturingMaturityCount',
      width: 180,
      sorter: (a, b) => a.manufacturingMaturityCount - b.manufacturingMaturityCount
    },
    {
      title: t('manufacturing_maturity_risk'),
      dataIndex: 'riskAssessment',
      key: 'manufacturingMaturityRisk',
      width: 180,
      render: (riskAssessment) => {
        const risk = riskAssessment?.manufacturingMaturityRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: t('target_kpi_achievement'),
      dataIndex: 'targetKPIAchievement',
      key: 'targetKPIAchievement',
      width: 150,
      sorter: (a, b) => a.targetKPIAchievement - b.targetKPIAchievement,
      render: (kpi) => (
        <Tag color={kpi >= 80 ? 'success' : kpi >= 70 ? 'warning' : 'error'}>
          {kpi}%
        </Tag>
      )
    },
    {
      title: t('kpi_risk'),
      dataIndex: 'riskAssessment',
      key: 'kpiRisk',
      width: 100,
      render: (riskAssessment) => {
        const risk = riskAssessment?.kpiRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: t('capa_closure_days'),
      dataIndex: 'capaClosureDays',
      key: 'capaClosureDays',
      width: 140,
      sorter: (a, b) => a.capaClosureDays - b.capaClosureDays
    },
    {
      title: t('capa_risk'),
      dataIndex: 'riskAssessment',
      key: 'capaRisk',
      width: 100,
      render: (riskAssessment) => {
        const risk = riskAssessment?.capaRisk;
        return (
          <Tag color={getRiskColor(risk)} icon={getRiskIcon(risk)}>
            {risk || 'N/A'}
          </Tag>
        );
      }
    },
    {
      title: 'IATF 16949',
      dataIndex: 'hasIATF16949',
      key: 'hasIATF16949',
      width: 120,
      render: (has) => (
        <Tag color={has ? 'success' : 'default'} icon={has ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {has ? t('yes') : t('no')}
        </Tag>
      )
    },
    {
      title: 'ISO 14001',
      dataIndex: 'hasISO14001',
      key: 'hasISO14001',
      width: 120,
      render: (has) => (
        <Tag color={has ? 'success' : 'default'} icon={has ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {has ? t('yes') : t('no')}
        </Tag>
      )
    },
    {
      title: t('new_supplier'),
      dataIndex: 'isNewSupplier',
      key: 'isNewSupplier',
      width: 120,
      render: (isNew) => (
        <Tag color={isNew ? 'blue' : 'default'}>
          {isNew ? t('yes') : t('no')}
        </Tag>
      )
    },
    {
      title: t('total_score'),
      dataIndex: 'totalScore',
      key: 'totalScore',
      width: 120,
      sorter: (a, b) => a.totalScore - b.totalScore,
      render: (score) => (
        <Tag color="processing" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {score?.toFixed(2) || 'N/A'}
        </Tag>
      )
    },
    {
      title: t('supplier_class'),
      dataIndex: 'supplierClass',
      key: 'supplierClass',
      width: 120,
      sorter: (a, b) => (a.supplierClass || '').localeCompare(b.supplierClass || ''),
      render: (supplierClass) => (
        <Tag color={getClassColor(supplierClass)} style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {supplierClass || 'N/A'}
        </Tag>
      )
    },
    {
      title: t('audit_frequency'),
      dataIndex: 'auditFrequency',
      key: 'auditFrequency',
      width: 140,
      render: (freq) => (
        <Tag color="cyan">
          {freq ? `${freq}x/${t('year')}` : 'N/A'}
        </Tag>
      )
    },
    {
      title: t('total_risk_level'),
      dataIndex: 'riskAssessment',
      key: 'totalRiskLevel',
      width: 150,
      render: (riskAssessment) => {
        if (!riskAssessment) return <Tag>N/A</Tag>;
        return (
          <Tag color={getRiskLevelColor(riskAssessment.totalRiskLevel)}>
            {riskAssessment.totalRiskLevel} ({riskAssessment.totalRiskScore})
          </Tag>
        );
      }
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('view_details')}>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => showDetailModal(record)}
            />
          </Tooltip>
          <Tooltip title={t('edit')}>
            <Button
              type="default"
              icon={<EditOutlined />}
              size="small"
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('delete_performance_data_confirm')}
            description={`${record.supplierName} - ${record.period}`}
            onConfirm={() => handleDelete(record.performanceDataId)}
            okText={t('yes')}
            cancelText={t('no')}
            okButtonProps={{ danger: true }}
          >
            <Tooltip title={t('delete')}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // ✅ Summary statistics
  const calculateStats = () => {
    const total = filteredData.length;
    const classA = filteredData.filter(d => d.supplierClass === 'A').length;
    const classB = filteredData.filter(d => d.supplierClass === 'B').length;
    const classC = filteredData.filter(d => d.supplierClass === 'C').length;
    const avgScore = filteredData.length > 0
      ? (filteredData.reduce((sum, d) => sum + (d.totalScore || 0), 0) / filteredData.length).toFixed(2)
      : 0;

    return { total, classA, classB, classC, avgScore };
  };

  const stats = calculateStats();

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        padding: '32px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderLeft: '4px solid #1890ff'
      }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#262626' }}>
              <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
              {t('supplier_performance_data_management')}
            </Title>
            <Text type="secondary" style={{ fontSize: '15px' }}>
              {t('view_and_manage_supplier_performance_records')}
            </Text>
          </Col>
          <Col>
            <Space size="middle">
              {/* ✅ YENİ BUTTON - FULLSCREEN TABLE */}
              <Button
                type="default"
                icon={<FullscreenOutlined />}
                size="large"
                onClick={() => setFullscreenModalVisible(true)}
                style={{ fontWeight: '600', borderColor: '#722ed1', color: '#722ed1' }}
              >
                {t('fullscreen_view')}
              </Button>
              
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => navigate('/admin-dashboard/performance-data-entry')}
                style={{ fontWeight: '600' }}
              >
                {t('add_new_entry')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                size="large"
                onClick={fetchPerformanceData}
              >
                {t('refresh')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                size="large"
                onClick={() => message.info(t('feature_coming_soon'))}
              >
                {t('export_excel')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('total_records')}
              value={stats.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={`${t('supplier_class')} A`}
              value={stats.classA}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={`${t('supplier_class')} B`}
              value={stats.classB}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('average_score')}
              value={stats.avgScore}
              suffix="/ 100"
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('filter_by_supplier')}
              allowClear
              onChange={handleSupplierChange}
              style={{ width: '100%' }}
              size="large"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {suppliers.map(supplier => (
                <Option key={supplier.supplierId} value={supplier.supplierId}>
                  {supplier.supplierName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('filter_by_period')}
              allowClear
              onChange={handlePeriodChange}
              style={{ width: '100%' }}
              size="large"
            >
              {periods.map(period => (
                <Option key={period} value={period}>
                  {period}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('filter_by_class')}
              allowClear
              onChange={handleClassChange}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="A">
                <Tag color="success">A - {t('excellent')}</Tag>
              </Option>
              <Option value="B">
                <Tag color="warning">B - {t('good')}</Tag>
              </Option>
              <Option value="C">
                <Tag color="error">C - {t('needs_improvement')}</Tag>
              </Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder={t('search_by_supplier')}
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
              size="large"
            />
          </Col>
        </Row>
      </Card>

      {/* Table - NORMAL VIEW (Compact) */}
      <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          scroll={{ x: 4500 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${t('total')} ${total} ${t('records')}`
          }}
        />
      </Card>

      {/* ✅ FULLSCREEN TABLE MODAL */}
      <Modal
        title={
          <Space>
            <FullscreenOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>
              {t('fullscreen_table_view')}
            </span>
          </Space>
        }
        open={fullscreenModalVisible}
        onCancel={() => setFullscreenModalVisible(false)}
        footer={[
          <Button 
            key="close" 
            type="primary" 
            icon={<FullscreenExitOutlined />}
            onClick={() => setFullscreenModalVisible(false)}
            size="large"
          >
            {t('close_fullscreen')}
          </Button>
        ]}
        width="95vw"
        style={{ top: 20, maxWidth: '95vw' }}
        bodyStyle={{ 
          padding: '16px',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }}
      >
        <Table
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${t('total')} ${total} ${t('records')}`
          }}
          size="small"
        />
      </Modal>

      {/* ✅ View Details Modal - TEMİZ VE DÜZENLİ */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>
              {t('performance_details')}
            </span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setAttachments([]);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>
            {t('close')}
          </Button>
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {selectedRecord && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}>
            {/* Supplier Info */}
            <Card 
              size="small" 
              style={{ marginBottom: '16px', background: '#f0f5ff' }}
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#1890ff' }} />
                  <span>{selectedRecord.supplierName}</span>
                </Space>
              }
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label={t('supplier_code')}>{selectedRecord.supplierCode}</Descriptions.Item>
                <Descriptions.Item label={t('period')}><Tag color="blue">{selectedRecord.period}</Tag></Descriptions.Item>
                <Descriptions.Item label={t('total_score')}>
                  <Tag color="processing" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {selectedRecord.totalScore?.toFixed(2)} / 100
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('supplier_class')}>
                  <Tag color={getClassColor(selectedRecord.supplierClass)} style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {selectedRecord.supplierClass}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Quality Metrics */}
            <Card size="small" title={t('quality_metrics')} style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label={t('product_revenue_usd')}>
                  ${selectedRecord.productRevenue?.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label={t('product_quantity')}>
                  {selectedRecord.productQuantity?.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label={t('defect_product_quantity')}>
                  {selectedRecord.defectProductQuantity}
                </Descriptions.Item>
                <Descriptions.Item label="PPM">
                  <Tag color={selectedRecord.ppm > 100 ? 'error' : 'success'}>
                    {selectedRecord.ppm?.toFixed(2)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('target_ppm')}>
                  {selectedRecord.targetPPM}
                </Descriptions.Item>
                <Descriptions.Item label={t('return_cost')}>
                  ${selectedRecord.returnCost?.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label={t('return_cost_paid')}>
                  <Tag color={selectedRecord.returnCostPaid ? 'success' : 'default'}>
                    {selectedRecord.returnCostPaid ? t('yes') : t('no')}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('audit_performance')}>
                  <Tag color={selectedRecord.auditPerformance >= 80 ? 'success' : 'warning'}>
                    {selectedRecord.auditPerformance}%
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Development Metrics */}
            <Card size="small" title={t('development_metrics')} style={{ marginBottom: '16px' }}>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label={t('product_safety')}>
                  <Tag color={getRiskColor(selectedRecord.productSafety)}>
                    {selectedRecord.productSafety}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('e_spice_deviations')}>
                  <Tag color={getRiskColor(selectedRecord.eSPICEDeviations)}>
                    {selectedRecord.eSPICEDeviations}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('developed_projects_count')}>
                  {selectedRecord.developedProjectsCount}
                </Descriptions.Item>
                <Descriptions.Item label={t('manufacturing_maturity_count')}>
                  {selectedRecord.manufacturingMaturityCount}
                </Descriptions.Item>
                <Descriptions.Item label={t('target_kpi_achievement')}>
                  <Tag color={selectedRecord.targetKPIAchievement >= 80 ? 'success' : 'warning'}>
                    {selectedRecord.targetKPIAchievement}%
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('capa_closure_days')}>
                  {selectedRecord.capaClosureDays}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Risk Assessment */}
            {selectedRecord.riskAssessment && (
              <Card size="small" title={t('risk_assessment')} style={{ marginBottom: '16px' }}>
                <Row gutter={[8, 8]}>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center', background: '#fafafa' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('product_quantity_risk')}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag color={getRiskColor(selectedRecord.riskAssessment.productQuantityRisk)} icon={getRiskIcon(selectedRecord.riskAssessment.productQuantityRisk)}>
                          {selectedRecord.riskAssessment.productQuantityRisk}
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center', background: '#fafafa' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('ppm_risk')}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag color={getRiskColor(selectedRecord.riskAssessment.ppmRisk)} icon={getRiskIcon(selectedRecord.riskAssessment.ppmRisk)}>
                          {selectedRecord.riskAssessment.ppmRisk}
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center', background: '#fafafa' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('audit_risk')}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag color={getRiskColor(selectedRecord.riskAssessment.auditRisk)} icon={getRiskIcon(selectedRecord.riskAssessment.auditRisk)}>
                          {selectedRecord.riskAssessment.auditRisk}
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                  <Col span={24}>
                    <Card size="small" style={{ textAlign: 'center', background: '#fff1f0' }}>
                      <Text strong>{t('total_risk_level')}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Tag color={getRiskLevelColor(selectedRecord.riskAssessment.totalRiskLevel)} style={{ fontSize: '16px', padding: '4px 12px' }}>
                          {selectedRecord.riskAssessment.totalRiskLevel} ({selectedRecord.riskAssessment.totalRiskScore})
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Certifications */}
            <Card size="small" title={t('certifications')} style={{ marginBottom: '16px' }}>
              <Space size="large">
                <div>
                  <Text strong>IATF 16949: </Text>
                  <Tag color={selectedRecord.hasIATF16949 ? 'success' : 'default'} icon={selectedRecord.hasIATF16949 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {selectedRecord.hasIATF16949 ? t('yes') : t('no')}
                  </Tag>
                </div>
                <div>
                  <Text strong>ISO 14001: </Text>
                  <Tag color={selectedRecord.hasISO14001 ? 'success' : 'default'} icon={selectedRecord.hasISO14001 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {selectedRecord.hasISO14001 ? t('yes') : t('no')}
                  </Tag>
                </div>
                <div>
                  <Text strong>{t('new_supplier')}: </Text>
                  <Tag color={selectedRecord.isNewSupplier ? 'blue' : 'default'}>
                    {selectedRecord.isNewSupplier ? t('yes') : t('no')}
                  </Tag>
                </div>
              </Space>
            </Card>

            {/* ✅ Attachments - TEMİZ VE DÜZENLI */}
            <Card 
              size="small" 
              title={
                <Space>
                  <PaperClipOutlined style={{ color: '#1890ff' }} />
                  <span>{t('attachments')}</span>
                  <Badge count={attachments.length} showZero style={{ backgroundColor: '#52c41a' }} />
                </Space>
              }
            >
              {loadingAttachments ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <ReloadOutlined spin style={{ fontSize: '32px', color: '#1890ff' }} />
                  <div style={{ marginTop: '16px', color: '#8c8c8c' }}>{t('loading_attachments')}</div>
                </div>
              ) : attachments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', background: '#fafafa', borderRadius: '8px' }}>
                  <FolderOpenOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                  <div style={{ marginTop: '12px', color: '#8c8c8c', fontSize: '14px' }}>
                    {t('no_attachments_found')}
                  </div>
                </div>
              ) : (
                <List
                  dataSource={attachments}
                  renderItem={(file) => (
                    <List.Item
                      style={{ padding: '12px', background: '#fafafa', marginBottom: '8px', borderRadius: '8px' }}
                      actions={[
                        <Button
                          type="primary"
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadAttachment(file.attachmentId, file.fileName)}
                        >
                          {t('download')}
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<FileOutlined style={{ fontSize: '28px', color: '#1890ff' }} />}
                        title={<Text strong>{file.fileName}</Text>}
                        description={
                          <Space direction="vertical" size={2}>
                            <Space size="small">
                              <Tag color="blue">{file.fieldName}</Tag>
                              <Tag color="green">{formatFileSize(file.fileSize)}</Tag>
                            </Space>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {moment(file.uploadedAt).format('DD/MM/YYYY HH:mm')}
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>
              {t('edit_performance_data')}
            </span>
          </Space>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            {t('cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleEditSave}
          >
            {t('save')}
          </Button>
        ]}
        width={900}
      >
        <Form form={form} layout="vertical">
          <Collapse defaultActiveKey={['1', '2', '3', '4']}>
            <Panel header={t('basic_information')} key="1">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="period" label={t('period')} rules={[{ required: true }]}>
                    <DatePicker.MonthPicker format="YYYY-MM" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="productRevenue" label={t('product_revenue_usd')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header={t('quality_metrics')} key="2">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="productQuantity" label={t('product_quantity')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="defectProductQuantity" label={t('defect_product_quantity')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="targetPPM" label={t('target_ppm')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="returnCost" label={t('return_cost')}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="returnCostPaid" label={t('return_cost_paid')} valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="auditPerformance" label={t('audit_performance')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header={t('development_metrics')} key="3">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="productSafety" label={t('product_safety')} rules={[{ required: true }]}>
                    <Select>
                      <Option value={1}><Tag color="success">1 - {t('low_risk')}</Tag></Option>
                      <Option value={9}><Tag color="error">9 - {t('high_risk')}</Tag></Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="eSPICEDeviations" label={t('e_spice_deviations')} rules={[{ required: true }]}>
                    <Select>
                      <Option value={1}><Tag color="success">1 - {t('low_risk')}</Tag></Option>
                      <Option value={9}><Tag color="error">9 - {t('high_risk')}</Tag></Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="developedProjectsCount" label={t('developed_projects_count')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="manufacturingMaturityCount" label={t('manufacturing_maturity_count')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} max={65} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="targetKPIAchievement" label={t('target_kpi_achievement')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="capaClosureDays" label={t('capa_closure_days')} rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>

            <Panel header={t('certifications')} key="4">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="isNewSupplier" label={t('new_supplier')} valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hasIATF16949" label="IATF 16949" valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="hasISO14001" label="ISO 14001" valuePropName="checked">
                    <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierPerformanceDataList;
